import { useCallback, useEffect } from 'react';
import { DEFAULT_FILTER_VALUES, DEFAULT_SORT_VALUE } from '../constants/filters';
import { UserTicketViewOption } from '../data/types';
import { usePortalData } from '../data/usePortalData';
import { useListingsSortAndFiltersContext } from './useListingsSortAndFiltersContext';
export const useTicketListingsSortAndFilters = () => {
  const {
    data
  } = usePortalData();
  const {
    view,
    sort,
    offset,
    createDateFilter,
    defaults,
    lastActivityFilter,
    statusFilter,
    ticketView,
    searchString,
    conversationSearch,
    setFiltersState
  } = useListingsSortAndFiltersContext();
  const setView = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    view: value
  })), [setFiltersState]);
  const setSort = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    sort: value.length > 0 ? value : DEFAULT_SORT_VALUE,
    offset: 0
  })), [setFiltersState]);
  const setOffset = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    offset: value
  })), [setFiltersState]);
  const setCreateDateFilter = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    createDateFilter: value,
    offset: 0
  })), [setFiltersState]);
  const setLastActivityFilter = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    lastActivityFilter: value,
    offset: 0
  })), [setFiltersState]);
  const setStatusFilter = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    statusFilter: value,
    offset: 0
  })), [setFiltersState]);
  const setTicketView = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    ticketView: value,
    offset: 0
  })), [setFiltersState]);
  const setSearchString = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    searchString: value,
    offset: 0
  })), [setFiltersState]);
  const setConversationSearch = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    conversationSearch: value
  })), [setFiltersState]);

  // Enforce constraints based on portal configuration
  useEffect(() => {
    if (!data) return;
    const {
      configuredTicketListProperties,
      userTicketViewOptions
    } = data;
    const validSortProperties = new Set(['createdate',
    //createdate is always a valid sortable property
    ...configuredTicketListProperties.map(p => p.propertyName)]);
    setFiltersState(state => {
      let next = state;
      if (!configuredTicketListProperties.find(({
        propertyName
      }) => propertyName === 'createdate') && state.createDateFilter !== DEFAULT_FILTER_VALUES.createDateFilterWithoutColumn) {
        next = Object.assign({}, next, {
          createDateFilter: DEFAULT_FILTER_VALUES.createDateFilterWithoutColumn
        });
      }
      if (!configuredTicketListProperties.find(({
        propertyName
      }) => propertyName === 'hs_lastactivitydate') && state.lastActivityFilter !== defaults.lastActivityFilter) {
        next = Object.assign({}, next, {
          lastActivityFilter: defaults.lastActivityFilter
        });
      }
      if (!userTicketViewOptions.includes(UserTicketViewOption.COMPANY) && state.ticketView !== UserTicketViewOption.PERSONAL) {
        next = Object.assign({}, next, {
          ticketView: UserTicketViewOption.PERSONAL
        });
      }
      const validSort = state.sort.filter(field => validSortProperties.has(field.propertyName));
      if (!validSort.length) {
        next = Object.assign({}, next, {
          sort: DEFAULT_SORT_VALUE
        });
      } else if (validSort.length !== state.sort.length) {
        next = Object.assign({}, next, {
          sort: validSort
        });
      }
      if (next !== state) {
        next = Object.assign({}, next, {
          offset: 0
        });
      }
      return next;
    });
  }, [data, defaults, setFiltersState]);
  const resetFilters = useCallback(() => {
    setFiltersState(state => Object.assign({}, state, {
      statusFilter: defaults.statusFilter,
      createDateFilter: defaults.createDateFilter,
      lastActivityFilter: defaults.lastActivityFilter,
      conversationSearch: '',
      offset: 0
    }));
  }, [setFiltersState, defaults]);
  const resetSort = useCallback(() => {
    setSort(DEFAULT_SORT_VALUE);
  }, [setSort]);
  const activeFilterCount = (statusFilter !== defaults.statusFilter ? 1 : 0) + (createDateFilter !== defaults.createDateFilter ? 1 : 0) + (lastActivityFilter !== defaults.lastActivityFilter ? 1 : 0) + (conversationSearch !== '' ? 1 : 0);
  return {
    view,
    sort,
    offset,
    createDateFilter,
    defaults,
    lastActivityFilter,
    statusFilter,
    ticketView,
    searchString,
    conversationSearch,
    setView,
    setSort,
    setOffset,
    setCreateDateFilter,
    setLastActivityFilter,
    setStatusFilter,
    setTicketView,
    setSearchString,
    setConversationSearch,
    resetFilters,
    resetSort,
    activeFilterCount
  };
};